import { getPlatformIcon } from "@utils/platform";

export type AskPageLinkItem = {
	platform: string;
	value: string;
};

export type AskPageLinksState = {
	items: AskPageLinkItem[];
};

type LinkPlatformDef = {
	addLabel: string;
	addSymbol: string;
	inputPrefix: string;
	placeholder: string;
};

const defs: Record<string, LinkPlatformDef> = {
	youtube: {
		addLabel: "YouTube",
		addSymbol: "play_arrow",
		inputPrefix: "youtube.com/@",
		placeholder: "handle",
	},
	instagram: {
		addLabel: "Instagram",
		addSymbol: "photo_camera",
		inputPrefix: "instagram.com/",
		placeholder: "handle",
	},
	tiktok: {
		addLabel: "TikTok",
		addSymbol: "music_note",
		inputPrefix: "tiktok.com/@",
		placeholder: "handle",
	},
	x: {
		addLabel: "X",
		addSymbol: "alternate_email",
		inputPrefix: "x.com/",
		placeholder: "handle",
	},
	linkedin: {
		addLabel: "LinkedIn",
		addSymbol: "work",
		inputPrefix: "linkedin.com/in/",
		placeholder: "handle",
	},
	facebook: {
		addLabel: "Facebook",
		addSymbol: "thumb_up",
		inputPrefix: "facebook.com/",
		placeholder: "handle",
	},
	twitch: {
		addLabel: "Twitch",
		addSymbol: "videogame_asset",
		inputPrefix: "twitch.tv/",
		placeholder: "channel",
	},
	website: {
		addLabel: "Website",
		addSymbol: "language",
		inputPrefix: "https://",
		placeholder: "yoursite.com",
	},
	blog: {
		addLabel: "Blog",
		addSymbol: "edit_note",
		inputPrefix: "https://",
		placeholder: "blog.example.com",
	},
	newsletter: {
		addLabel: "Newsletter",
		addSymbol: "mail",
		inputPrefix: "https://",
		placeholder: "newsletter.example.com",
	},
	podcast: {
		addLabel: "Podcast",
		addSymbol: "podcasts",
		inputPrefix: "https://",
		placeholder: "podcast.example.com",
	},
};

/** Platforms offered in the “Add link” menu (order matches design). */
export const ASK_PAGE_ADD_LINK_PLATFORMS = [
	"tiktok",
	"x",
	"linkedin",
	"facebook",
	"threads",
	"twitch",
	"website",
	"blog",
	"newsletter",
	"podcast",
] as const;

export const askPageLinkPlatformDef = (
	platform: string,
): LinkPlatformDef | undefined => defs[platform];

export const askPageLinkUsesBrandIcon = (platform: string) =>
	Boolean(getPlatformIcon(platform));

export const emptyAskPageLinks = (): AskPageLinksState => ({ items: [] });

const LEGACY_KEYS = ["youtube", "instagram", "x", "website"] as const;

/** Server pageVars / API may still send the legacy flat object. */
export const askPageLinksFromPageVars = (
	links:
		| AskPageLinksState
		| {
				items?: AskPageLinkItem[];
				youtube?: string | null;
				instagram?: string | null;
				x?: string | null;
				website?: string | null;
		  }
		| null
		| undefined,
): AskPageLinksState => {
	if (!links) {
		return emptyAskPageLinks();
	}
	if (Array.isArray(links.items)) {
		return {
			items: links.items.map((item) => ({
				platform: item.platform,
				value: item.value ?? "",
			})),
		};
	}
	const items: AskPageLinkItem[] = [];
	for (const platform of LEGACY_KEYS) {
		const raw = links[platform];
		if (typeof raw === "string" && raw.trim() !== "") {
			items.push({
				platform,
				value: suffixFromLegacyStored(platform, raw.trim()),
			});
		}
	}
	return { items };
};

export const sameAskPageLinks = (a: AskPageLinksState, b: AskPageLinksState) =>
	a.items.length === b.items.length &&
	a.items.every(
		(item, index) =>
			item.platform === b.items[index]?.platform &&
			item.value === b.items[index]?.value,
	);

function suffixFromLegacyStored(platform: string, stored: string): string {
	const def = defs[platform];
	if (!def) {
		return stored;
	}
	const prefix = def.inputPrefix.toLowerCase();
	const lower = stored.toLowerCase();
	if (lower.startsWith(prefix)) {
		return stored.slice(def.inputPrefix.length).replace(/^[@/]+/, "");
	}
	if (lower.startsWith("https://")) {
		return stored.replace(/^https?:\/\//i, "");
	}
	return stored;
}
